import * as React from 'react';

import { Title } from '../../utils/title';
import { useThemeSwitcher } from 'react-css-theme-switcher';
import { Link } from 'react-router-dom';
import { formatNumber } from '@progress/kendo-intl';
import { useEffect, useRef, useState } from 'react';
import { fetchApi } from 'services/api';
import { Grid, GridColumn, GridCustomCellProps, GridToolbar } from '@progress/kendo-react-grid';
import { ExcelExport, ExcelExportColumn as ExportColumn } from '@progress/kendo-react-excel-export';
import { GridLoadingPanel } from 'components/GridLoadingPanel';
import { Button } from '@progress/kendo-react-buttons';
import { Loader } from '@progress/kendo-react-indicators';
import PopupNavBar from 'components/Layout/PopupNavBar';
import { arrowRotateCwIcon, downloadIcon } from '@progress/kendo-svg-icons';
import CarrierReviews from 'views/CarrierReviews';
import SimpleDateRangePicker from 'components/SimpleDateRangePicker';
import { MultiSelect } from '@progress/kendo-react-dropdowns';
import { useSessionStorage } from 'usehooks-ts';
import dayjs from 'dayjs';
import { SortDescriptor } from '@progress/kendo-data-query';
import { useDebouncedCallback } from 'use-debounce';
import CarrierAutoComplete from 'views/AssetCarriers/CarrierAutoComplete';

type Response = {
  Cards: Card[];
  CapacitySources: Array<{ SourceName: string, Count: number }>;
  Carriers: Carrier[];
}

type Card = {
  Title: string;
  FormattedValue: string;
  SubFormattedValue1: string;
  SubFormattedValue2: string;
  Change: string;
}

type Carrier = {
  CarrierID: number;
  CarrierName: string;
  Phone1: string;
  TotalPay: number;
  TotalCharges: number;
  MarginPercentage: number;
  TripCount: number;
  SecurementPercentage: number;
  TotalLoadedMiles: number;
  CargoVanAllRPM: number;
  SprinterAllRPM: number;
  StraightAllRPM: number;
  TractorAllRPM: number;
  CargoVanTripCount: number;
  SprinterTripCount: number;
  StraightTripCount: number;
  TractorTripCount: number;
  CargoVanNotWonRPM: number;
  SprinterNotWonRPM: number;
  StraightNotWonRPM: number;
  TractorNotWonRPM: number;
  ResponsePercentage: number | null;
  BidPercentage: number | null;
  DeclinePercentage: number | null;
  CarrierScore: number | null;
  OnTimePickupPercent: number | null;
  OnTimeDeliveryPercent: number | null;
}

const CarrierCell = (props: GridCustomCellProps) => {
  if (!props.field || props.rowType !== 'data') return null;
  const dataItem = props.dataItem as Carrier;
  return <td {...props.tdProps}>
    <Link style={{ color: "#007bff" }} to={`/Assets/Carrier/${dataItem.CarrierID}`}>{dataItem.CarrierName}</Link>
  </td>;
}

const RPMCell = (props: GridCustomCellProps) => {
  if (!props.field || props.rowType !== 'data') return null;
  const rpm = props.dataItem[props.field];
  return <td {...props.tdProps}>{rpm ? formatNumber(rpm, 'c') : 'N/A'}</td>;
}

const VehicleTypes = [
  { ID: 1, Name: 'Cargo Van' },
  { ID: 15, Name: 'Sprinter' },
  { ID: 2, Name: 'Small Straight' },
  { ID: 3, Name: 'Large Straight' },
  { ID: 4, Name: 'Tractor' },
];

const CarrierDashboard = () => {

  const _export = useRef<ExcelExport | null>(null);
  const [sort, setSort] = useState<SortDescriptor[]>([{ field: 'TotalPay', dir: 'desc' }]);
  const { status } = useThemeSwitcher();
  const [loading, setLoading] = useState(true);
  
  // Default date range: month to date
  const [fromDate, setFromDate] = useSessionStorage<Date>(
    "CarrierDashboard-FromDate",
    () => dayjs().tz("America/New_York").subtract(1, 'month').toDate(),
    {
      serializer: (value) => value ? value.toISOString() : null,
      deserializer: (value) => value ? new Date(value) : null,
    }
  );
  const [toDate, setToDate] = useSessionStorage<Date>(
    "CarrierDashboard-ToDate",
    () => dayjs().tz("America/New_York").add(1, 'day').startOf('day').toDate(),
    {
      serializer: (value) => value ? value.toISOString() : null,
      deserializer: (value) => value ? new Date(value) : null,
    }
  );
  const [vehicleTypeIds, setVehicleTypeIDs] = useSessionStorage<number[]>("CarrierDashboard-VehicleTypeIDs", []);
  const [carrierId, setCarrierID] = useSessionStorage<number | null>("CarrierDashboard-CarrierID", null);

  const [cards, setCards] = useState<Card[]>([]);
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [capacitySources, setCapacitySources] = useState<Array<{ SourceName: string, Count: number }>>([]);
  const [carrierReviews, setCarrierReviews] = useState<{ CarrierName: string, Phone1: string }>(null);

  const refresh = useDebouncedCallback((fromDate: Date, toDate: Date, vehicleTypeIds: number[], carrierId: number | null) => {
    setLoading(true);
    const data = {
      FromDate: fromDate,
      ToDate: toDate,
      VehicleTypeIDs: vehicleTypeIds,
      CarrierID: carrierId
    };
    fetchApi(`/api/Asset/CarrierDashboard`, data, 'POST')
      .then((response: Response) => {
        setCards(response.Cards);
        setCarriers(response.Carriers);
        setCapacitySources(response.CapacitySources);
      })
      .catch((e) => {
        if (!e?.status) alert('Error fetching data');
      })
      .finally(() => setLoading(false));
  }, 100);

  useEffect(() => {
    refresh(fromDate, toDate, vehicleTypeIds, carrierId);
  }, [refresh, fromDate, toDate, vehicleTypeIds, carrierId]);

  if (status !== 'loaded') {
    return <div>Loading...</div>;
  }

  return <React.Fragment>
    <PopupNavBar />
    <Title string="Carrier Dashboard" />
    {carrierReviews && <CarrierReviews
      CloseDialog={() => setCarrierReviews(null)}
      Name={carrierReviews.CarrierName}
      PhoneNumber={carrierReviews.Phone1}
    />}
    <nav className="navbar navbar-dark bg-dark">
      <Link to="/" className="navbar-brand">Carrier Dashboard {loading && <Loader type="pulsing" className="p-0" />}</Link>
      <ul className="nav navbar-nav navbar-right">
        <Button
          title="Refresh"
          icon="refresh"
          svgIcon={arrowRotateCwIcon}
          disabled={loading}
          onClick={() => refresh(fromDate, toDate, vehicleTypeIds, carrierId)}
        />
      </ul>
    </nav>

    <div className="container-fluid">
      <div className="row pt-2">
        <div className="col-md-4">
          <SimpleDateRangePicker
            inputProps={{ disabled: loading }}
            value={{ start: fromDate, end: toDate }}
            onChange={(e) => {
              setFromDate(e.start);
              setToDate(e.end);
            }}
          />
        </div>
        <div className="col-md-3">
          <MultiSelect
            placeholder="Select Vehicle Types"
            disabled={loading}
            data={VehicleTypes}
            textField="Name"
            dataItemKey="ID"
            value={VehicleTypes.filter(x => vehicleTypeIds.includes(x.ID))}
            onChange={(e) => setVehicleTypeIDs((e.value as any[]).map(x => x.ID))}
          />
        </div>
        <div className="col-md-3">
          <CarrierAutoComplete
            disabled={loading}
            selectedCarrierID={carrierId}
            onSelectedCarrier={c => {
              setCarrierID(c?.CarrierID ?? null);
            }}
          />
        </div>
      </div>
    </div>

    <div className="container-fluid">
      <div className="row">
        {cards.map((card, index) => {
          return <div key={index} className="col-sm-6 col-md-4 col-lg-3 col-xl-2 mt-3">
            <div className="card h-100">
              <div className="card-body">
                <h5 className="text-muted fw-normal mt-0">{card.Title}</h5>
                <h3 className="mt-3 mb-3">{card.FormattedValue}</h3>
                {card.SubFormattedValue1 && <p className="mb-1">{card.SubFormattedValue1}</p>}
                {card.SubFormattedValue2 && <p className="mb-1">{card.SubFormattedValue2}</p>}
                <p className="mb-0 text-muted">
                  <span className="text-nowrap">{card.Change}</span>
                </p>
              </div>
            </div>
          </div>
        })}
        {capacitySources.length > 0 && (
          <div className="col-sm-6 col-md-4 col-lg-3 col-xl-2 mt-3">
            <div className="card h-100">
              <div className="card-body">
                <table style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th className="text-muted">Source</th>
                      <th className="text-muted" style={{ textAlign: 'right' }}>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {capacitySources.map((data, index) => {
                      return <tr key={index}>
                        <td>{data.SourceName}</td>
                        <td style={{ textAlign: 'right' }}>{formatNumber(data.Count, "n0")}</td>
                      </tr>
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
      <br />
      {loading && <GridLoadingPanel />}
      <ExcelExport ref={_export} fileName={`Carrier Dashboard.xlsx`}>
        <ExportColumn field="CarrierName" title="Carrier" />
        <ExportColumn field="CarrierScore" title="Score" cellOptions={{ format: "#,##0" }} />
        <ExportColumn field="OnTimePickupPercent" title="On Time Pickup" cellOptions={{ format: "0.00%" }} />
        <ExportColumn field="OnTimeDeliveryPercent" title="On Time Delivery" cellOptions={{ format: "0.00%" }} />
        <ExportColumn field="TotalPay" title="Total Pay" cellOptions={{ format: "$#,##0.00" }} />
        <ExportColumn field="MarginPercentage" title="Margin %" cellOptions={{ format: "0.00%" }} />
        <ExportColumn field="ResponsePercentage" title="Response %" cellOptions={{ format: "0.00%" }} />
        <ExportColumn field="BidPercentage" title="Accept %" cellOptions={{ format: "0.00%" }} />
        <ExportColumn field="DeclinePercentage" title="Decline %" cellOptions={{ format: "0.00%" }} />
        <ExportColumn field="TripCount" title="Trip Count" cellOptions={{ format: "#,##0" }} />
        <ExportColumn field="CargoVanTripCount" title="Cargo Van Trip Count" cellOptions={{ format: "#,##0" }} />
        <ExportColumn field="SprinterTripCount" title="Sprinter Trip Count" cellOptions={{ format: "#,##0" }} />
        <ExportColumn field="StraightTripCount" title="Straight Trip Count" cellOptions={{ format: "#,##0" }} />
        <ExportColumn field="TractorTripCount" title="Tractor Trip Count" cellOptions={{ format: "#,##0" }} />
        <ExportColumn field="SecurementPercentage" title="Securement %" cellOptions={{ format: "0.00%" }} />
        <ExportColumn field="TotalLoadedMiles" title="Loaded Miles" cellOptions={{ format: "#,##0" }} />
        <ExportColumn field="CargoVanAllRPM" title="Cargo Van >400mi"  cellOptions={{ format: "$#,##0.00" }} />
        <ExportColumn field="SprinterAllRPM" title="Sprinter >400mi" cellOptions={{ format: "$#,##0.00" }} />
        <ExportColumn field="StraightAllRPM" title="Straight >400mi" cellOptions={{ format: "$#,##0.00" }} />
        <ExportColumn field="TractorAllRPM" title="Tractor >400mi" cellOptions={{ format: "$#,##0.00" }} />
        <ExportColumn field="CargoVanNotWonRPM" title="Cargo Van Not Won RPM" cellOptions={{ format: "$#,##0.00" }} />
        <ExportColumn field="SprinterNotWonRPM" title="Sprinter Not Won RPM" cellOptions={{ format: "$#,##0.00" }} />
        <ExportColumn field="StraightNotWonRPM" title="Straight Not Won RPM" cellOptions={{ format: "$#,##0.00" }} />
        <ExportColumn field="TractorNotWonRPM" title="Tractor Not Won RPM" cellOptions={{ format: "$#,##0.00" }} />
      </ExcelExport>
      <Grid
        sortable
        sort={sort}
        onSortChange={(e) => setSort(e.sort)}
        autoProcessData
        data={carriers}
      >
        <GridToolbar>
          <Button
            svgIcon={downloadIcon}
            onClick={() => _export.current?.save(carriers)}
          >Excel
          </Button>
        </GridToolbar>
        <GridColumn field="CarrierName" title="Carrier" cells={{ data: CarrierCell }} />
        <GridColumn
          field="CarrierScore"
          title="Score"
          width={90}
          cells={{
            data: (props) => (
              <td
                {...props.tdProps}
                style={{ cursor: "pointer", textAlign: "right" }}
                onClick={() => setCarrierReviews({ CarrierName: props.dataItem.CarrierName, Phone1: props.dataItem.Phone1 })}
              >
                {props.dataItem.CarrierScore != null ? formatNumber(props.dataItem.CarrierScore, "n0") : ""}
              </td>
            )
          }}
        />
        <GridColumn
          field="OnTimePickupPercent"
          title="On Time Pickup"
          cells={{ data: (props) => <td {...props.tdProps}>{props.dataItem.OnTimePickupPercent != null ? formatNumber(props.dataItem.OnTimePickupPercent, 'p2') : ""}</td> }}
        />
        <GridColumn
          field="OnTimeDeliveryPercent"
          title="On Time Delivery"
          cells={{ data: (props) => <td {...props.tdProps}>{props.dataItem.OnTimeDeliveryPercent != null ? formatNumber(props.dataItem.OnTimeDeliveryPercent, 'p2') : ""}</td> }}
        />
        <GridColumn field="TotalPay" title="Total Pay" format="{0:c}" />
        <GridColumn field="MarginPercentage" title="Margin %" format="{0:p2}" />
        <GridColumn field="BidPercentage" title="Accept %" format="{0:p2}" cells={{ data: (props) => <td {...props.tdProps}>{props.dataItem.BidPercentage !== null ? formatNumber(props.dataItem.BidPercentage, 'p2') : "N/A"}</td>}} />
        <GridColumn field="DeclinePercentage" title="Decline %" format="{0:p2}" cells={{ data: (props) => <td {...props.tdProps}>{props.dataItem.DeclinePercentage !== null ? formatNumber(props.dataItem.DeclinePercentage, 'p2') : "N/A"}</td>}} />
        <GridColumn field="TripCount" title="Trip Count" />
        <GridColumn field="SecurementPercentage" title="Secure %" format="{0:p2}" />
        <GridColumn field="TotalLoadedMiles" title="Loaded Miles" format="{0:n0} mi" />
        <GridColumn field="CargoVanAllRPM" title="Cargo Van >400mi" cells={{ data: RPMCell }} />
        <GridColumn field="SprinterAllRPM" title="Sprinter >400mi" cells={{ data: RPMCell }} />
        <GridColumn field="StraightAllRPM" title="Straight >400mi" cells={{ data: RPMCell }} />
        <GridColumn field="TractorAllRPM" title="Tractor >400mi" cells={{ data: RPMCell }} />
      </Grid>
    </div>
  </React.Fragment>
}

export default CarrierDashboard;
